import { logError } from '../lib/logger';
import { emit as debug } from '../lib/debugBus';
import React, { useState, useEffect, useCallback, useRef, useMemo, useDeferredValue } from 'react';
import axios from 'axios';
import ContentCarousel from '../components/ContentCarousel';
import VideoModal from '../components/VideoModal';
import DocumentModal from '../components/DocumentModal';
import HomeBanner from '../components/HomeBanner';
import DueRemindersBanner from '../components/business/DueRemindersBanner';
import { Input } from '../components/ui/input';
import { Loader2, Layers, Search, X } from 'lucide-react';
import { getNonEmptyCategoryGroups } from '../lib/contentOrder';
import { APP_NAME, IS_MAGIC_TEAM } from '../lib/brand';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Mettere a true per mostrare la ricerca a tutti gli utenti. */
const HOME_CONTENT_SEARCH_FOR_ALL = true;

function normalizeSearchText(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

function contentMatchesQuery(content, query) {
  if (!query) return true;
  const haystack = normalizeSearchText(
    `${content?.title || ''} ${content?.description || ''}`,
  );
  return query.split(/\s+/).filter(Boolean).every((token) => haystack.includes(token));
}

const Home = () => {
  const { isAdmin, canAccessBusiness } = useAuth();
  const showContentSearch = isAdmin || HOME_CONTENT_SEARCH_FOR_ALL;
  const [categories, setCategories] = useState([]);
  const [contents, setContents] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const deferredSearch = useDeferredValue(searchQuery);
  const [selectedContent, setSelectedContent] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const modalOpenRef = useRef(false);
  modalOpenRef.current = Boolean(selectedContent || selectedDocument);

  const fetchData = useCallback(async () => {
    setLoadError('');
    try {
      const [catRes, contRes, notifRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/contents`),
        axios.get(`${API}/notifications/feed`).catch((err) => { logError('Home:notifications', err); return null; }),
      ]);
      setCategories(catRes.data);
      setContents(contRes.data);
      if (notifRes) setNotifications(notifRes.data?.notifications || []);
    } catch (err) {
      logError('Home', err);
      setLoadError('Impossibile caricare i contenuti. Controlla la connessione e riprova.');
    } finally {
      setLoading(false);
    }
  }, []);

  /** Il popup banner (Header) spara questo evento appena l'utente lo chiude,
   * così la fascia in home compare subito senza aspettare un refetch/reload. */
  useEffect(() => {
    const handleBannerReady = (event) => {
      const notif = event.detail;
      if (!notif?.id) return;
      setNotifications((prev) => {
        const idx = prev.findIndex((n) => n.id === notif.id);
        if (idx === -1) return [notif, ...prev];
        const next = [...prev];
        next[idx] = { ...next[idx], ...notif };
        return next;
      });
    };
    window.addEventListener('mtc-banner-ready', handleBannerReady);
    return () => window.removeEventListener('mtc-banner-ready', handleBannerReady);
  }, []);

  /** Un banner appare in home solo dopo che l'utente ha chiuso il popup
   * (notif.read) e resta finché non supera la data/ora impostata dall'admin
   * (non è chiudibile manualmente). */
  const activeBanner = useMemo(() => {
    const now = Date.now();
    return notifications.find((n) => {
      if (IS_MAGIC_TEAM) return false;
      if (!n.is_banner || !n.read || !n.image_path || !n.banner_expires_at) return false;
      const expiresAt = new Date(n.banner_expires_at).getTime();
      return Number.isFinite(expiresAt) && expiresAt > now;
    }) || null;
  }, [notifications]);

  const lastForegroundFetchRef = useRef(0);

  const runForegroundRefresh = useCallback(() => {
    if (document.visibilityState !== 'visible') return;
    if (modalOpenRef.current) return;
    const now = Date.now();
    if (now - lastForegroundFetchRef.current < 800) return;
    lastForegroundFetchRef.current = now;
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchData();

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') runForegroundRefresh();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', runForegroundRefresh);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', runForegroundRefresh);
    };
  }, [fetchData, runForegroundRefresh]);

  const handleContentClick = (content) => {
    debug('home:tap', { id: content.id, type: content.content_type, title: content.title });
    if (content.content_type === 'video') {
      setSelectedContent(content);
    } else if (content.file_path) {
      setSelectedDocument(content);
    }
  };

  useEffect(() => {
    debug('home:selectedContent', { id: selectedContent?.id || null });
  }, [selectedContent]);

  useEffect(() => {
    debug('home:mount');
    return () => debug('home:unmount');
  }, []);

  const normalizedQuery = useMemo(
    () => (showContentSearch ? normalizeSearchText(deferredSearch) : ''),
    [showContentSearch, deferredSearch],
  );
  const filteredContents = useMemo(() => {
    if (!normalizedQuery) return contents;
    return contents.filter((c) => contentMatchesQuery(c, normalizedQuery));
  }, [contents, normalizedQuery]);
  const categoryGroups = getNonEmptyCategoryGroups(filteredContents, categories);
  const hasContent = categoryGroups.length > 0;
  const hasCatalog = contents.length > 0;
  const hasNoContent = !loadError && !hasCatalog;
  const hasNoSearchResults = !loadError && hasCatalog && !!normalizedQuery && !hasContent;

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  return (
    <>
      <main className="max-w-7xl mx-auto px-4 py-6">
        {loadError && (
          <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-4 text-center" role="alert">
            <p className="text-red-800 text-sm mb-3">{loadError}</p>
            <button
              type="button"
              onClick={() => { setLoading(true); fetchData(); }}
              className="px-4 py-2 rounded-full bg-[#D4AF37] hover:bg-[#B8941F] text-white text-sm font-medium"
            >
              Riprova
            </button>
          </div>
        )}

        <DueRemindersBanner enabled={canAccessBusiness} className="mb-6" />

        {showContentSearch && hasCatalog && (
          <div className="mb-6" data-testid="home-content-search">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
              <Input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Cerca nei titoli e nelle descrizioni…"
                className="pl-9 pr-10 rounded-full bg-white border-zinc-200 text-zinc-900"
                aria-label="Cerca contenuti"
                data-testid="home-content-search-input"
              />
              {searchQuery ? (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                  aria-label="Cancella ricerca"
                  data-testid="home-content-search-clear"
                >
                  <X className="w-4 h-4" />
                </button>
              ) : null}
            </div>
            {isAdmin && !HOME_CONTENT_SEARCH_FOR_ALL ? (
              <p className="mt-1.5 text-[11px] text-zinc-400">Visibile solo agli admin (test)</p>
            ) : null}
          </div>
        )}

        {activeBanner && (
          <HomeBanner notification={activeBanner} />
        )}

        {hasNoContent && (
          <div className="text-center py-20" data-testid="empty-state">
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center">
              <Layers className="w-10 h-10 text-[#D4AF37]" />
            </div>
            <h2 className="text-2xl font-light text-zinc-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Benvenuto in <span className="text-[#D4AF37]">{APP_NAME}</span>
            </h2>
            <p className="text-zinc-600 max-w-md mx-auto">
              Nessun contenuto disponibile al momento. Riceverai una notifica appena verranno caricati nuovi materiali formativi.
            </p>
          </div>
        )}

        {hasNoSearchResults && (
          <div className="text-center py-16" data-testid="home-search-empty">
            <p className="text-zinc-700 text-sm">
              Nessun contenuto trovato per «{searchQuery.trim()}».
            </p>
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="mt-3 text-sm font-medium text-[#D4AF37] hover:text-[#B8941F]"
            >
              Cancella ricerca
            </button>
          </div>
        )}

        {hasContent && (
          <section className="mb-12" data-testid="content-section">
            <div className="flex items-center gap-3 mb-6 pb-3 border-b border-zinc-200">
              <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
                <Layers className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <h2 className="text-2xl font-medium text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Formazione
                </h2>
                <p className="text-zinc-500 text-sm">
                  {normalizedQuery
                    ? `${filteredContents.length} risultat${filteredContents.length === 1 ? 'o' : 'i'} per la ricerca`
                    : 'Video e documenti per argomento'}
                </p>
              </div>
            </div>
            {categoryGroups.map(({ category, items }, index) => (
              <ContentCarousel
                key={category.id}
                title={category.name}
                contents={items}
                onContentClick={handleContentClick}
                delay={index * 0.05}
              />
            ))}
          </section>
        )}

      </main>

      {selectedContent && (
        <VideoModal content={selectedContent} engagementSource="main" onClose={() => setSelectedContent(null)} />
      )}

      {selectedDocument && (
        <DocumentModal content={selectedDocument} engagementSource="main" onClose={() => setSelectedDocument(null)} />
      )}
    </>
  );
};

export default Home;
